import type { TypedDocumentNode } from '@apollo/client';
import { gql, useSuspenseQuery } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { Box } from 'atoms/icons/Box';
import { Cards } from 'atoms/icons/Cards';
import { Team } from 'atoms/icons/Team';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyL, BrandM, BrandXXL } from 'atoms/typography';
import { CardImg } from 'components/card/CardImg';

import type {
  HowToPlayCardsQuery,
  HowToPlayCardsQueryVariables,
  // eslint-disable-next-line import-x/extensions
} from './__generated__/index.graphql';
import bg from './assets/bg.webp';

const Root = styled(Vertical).attrs({
  center: true,
})`
  width: var(--100vw);
  background-image: url(${bg});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  mask-image: linear-gradient(to bottom, black 90%, transparent 100%);
`;

const Container = styled(Vertical).attrs({
  center: true,
  gap: 8,
})`
  padding-top: calc(var(--unit) * 10);
  padding-bottom: calc(var(--unit) * 10);
`;

const IconWrapper = styled(Horizontal)`
  margin-right: var(--unit);
`;

const CardContainer = styled(Horizontal).attrs({ gap: 2, center: true })`
  & > * {
    aspect-ratio: var(--card-aspect-ratio);
    max-width: 160px;
  }
`;

const heroDescription = defineMessages({
  collect: {
    id: 'HowToPlay.Hero.description.collect',
    defaultMessage: 'Collect <bold>officially licensed sports cards</bold>',
    values: {
      bold: (chunks: ReactNode) => <strong>{chunks}</strong>,
    },
  },
  build: {
    id: 'HowToPlay.Hero.description.build',
    defaultMessage: 'Build your dream teams',
  },
  compete: {
    id: 'HowToPlay.Hero.description.compete',
    defaultMessage: 'Compete for <bold>incredible rewards</bold>',
    values: {
      bold: (chunks: ReactNode) => <strong>{chunks}</strong>,
    },
  },
});

const heroIcons = {
  collect: Cards,
  build: Team,
  compete: Box,
};

const HOW_TO_PLAYCARDS_QUERY = gql`
  query HowToPlayCardsQuery($slugs: [String!]!) {
    anyCards(slugs: $slugs) {
      slug
      anyPositions
      pictureUrl
      score: averageScore(type: LAST_FIVE_SO5_AVERAGE_SCORE)
    }
  }
` as TypedDocumentNode<HowToPlayCardsQuery, HowToPlayCardsQueryVariables>;

export const Hero = ({ cards }: { cards: string[] }) => {
  const { data } = useSuspenseQuery(HOW_TO_PLAYCARDS_QUERY, {
    variables: { slugs: cards },
  });

  return (
    <Root>
      <Container>
        <Vertical center gap={6}>
          <Vertical gap={3} center>
            <BrandM uppercase>
              <FormattedMessage
                id="HowToPlay.Hero.title"
                defaultMessage="Fantasy sports meets trading cards"
              />
            </BrandM>
            <BrandXXL
              uppercase
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': `linear-gradient(
        45deg,
        #f8d3da,
        #b3a9f4 28%,
        #fbe9fb 54%,
        #4f94fd 100%
      )`,
                } as CSSProperties
              }
            >
              <FormattedMessage
                id="HowToPlay.Hero.subtitle"
                defaultMessage="You're the manager"
              />
            </BrandXXL>
          </Vertical>
          <Horizontal gap={8} center>
            {(
              Object.keys(heroDescription) as Array<
                keyof typeof heroDescription
              >
            ).map(key => {
              const message = heroDescription[key];
              const Icon = heroIcons[key];
              return (
                <Horizontal key={message.id} gap={0} center>
                  <IconWrapper style={{ color: 'var(--c-blue-400)' }}>
                    <Icon />
                  </IconWrapper>
                  <BodyL>
                    <FormattedMessage {...message} />
                  </BodyL>
                </Horizontal>
              );
            })}
          </Horizontal>
        </Vertical>
        <CardContainer gap={2}>
          {data.anyCards.map(card => (
            <CardImg key={card.slug} src={card.pictureUrl} width={160} />
          ))}
        </CardContainer>
      </Container>
    </Root>
  );
};
